<template>
  <div class="pt-50">
    <ProfileListAddv2 :create-profile-active.sync="createProfileActive" @refetch-data="refetchData" />

    <div class="content-px mb-1">
      <b-row>
        <b-col cols="12">
          <div class="d-flex align-items-center justify-content-between w-100">
            <div class="d-flex align-items-center justify-content-start flex-wrap flex-sm-nowrap" style="gap: 0.675rem">
              <div style="max-width: 18rem; min-width: 15rem; width: 100%">
                <label>Search</label>
                <b-form-input v-model="searchQuery" class="drop-shadow-default w-full" placeholder="Search..." />
              </div>
              <!-- Filters -->
              <profile-list-filters
                :include-enrolled-filter.sync="includeEnrolledFilter"
                :status-filter.sync="statusFilter"
                :status-options="statusOptions"
                :create-at-start-filter.sync="createAtStartFilter"
                :create-at-end-filter.sync="createAtEndFilter"
                :funding-filter.sync="fundingFilter"
                :funding-options="fundingSourceOptions"
                :resume-filter.sync="resumeFilter"
                :resume-options="uploadedResumeOptions"
                :coe-filter.sync="coeFilter"
                :coe-options="uploadedCoeOptions"
                :submission-filter.sync="submissionFilter"
                :submission-options="submittedToVaOptions"
                :foresight-filter.sync="foresightFilter"
                :foresight-options="foresightOptions"
              />
            </div>

            <b-button
              v-if="$can('roster.profile.create')"
              variant="primary"
              @click="newProfile"
              class="ml-1"
              style="line-height: 1rem; font-size: 1rem"
            >
              <feather-icon icon="PlusCircleIcon" size="16" class="mr-25" />
              <span class="text-nowrap">New Profile</span>
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Table Container Card -->
    <div no-body class="mb-0 table-no-top">
      <b-table
        ref="refProfileListTable"
        class="position-relative mb-0"
        :items.sync="fetchProfiles"
        responsive
        :fields="tableColumnsList"
        :filter.sync="searchQuery"
        :filterByFormatted="true"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :current-page.sync="currentPage"
        :per-page.sync="perPage"
        @row-contextmenu="onRowContextMenu"
      >
        <!-- Column: Session -->
        <template #cell(studentId)="data">
          {{ data.item.student.myIpId }}
        </template>

        <!-- Column: Session -->
        <template #cell(id)="data">
          <b-link
            :to="{ name: 'apps-students-view', params: { id: data.item.student.id } }"
            class="font-weight-bold text-nowrap text-white mb-0"
            style="font-size: 1.25rem; line-height: 1.75rem"
          >
            {{ data.item.student.fullName }}
          </b-link>
          <b-link :to="{ name: 'apps-students-view', params: { id: data.item.student.id } }" class="d-flex align-items-center text-muted">
            <small style="font-size: 1.05rem; line-height: 1.35rem">{{ data.item.student.myIpId }}</small>
          </b-link>
        </template>
        <!-- Column: Session -->
        <template #cell(rep)="data">
          <div class="text-nowrap" v-if="data.item.representative">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="32"
                  :src="data.item.representative.avatar"
                  :text="avatarText(data.item.representative.name)"
                  :variant="`light-${resolveUserRoleVariant(data.item.representative.role)}`"
                  :to="{ name: 'apps-users-view', params: { id: data.item.representative.id } }"
                />
              </template>
              <b-link
                :to="{ name: 'apps-users-view', params: { id: data.item.representative.id } }"
                class="font-weight-bold d-block text-nowrap"
              >
                {{ data.item.representative.name }}
              </b-link>
              <small class="text-muted">@{{ data.item.representative.username }}</small>
            </b-media>
          </div>
          <div class="text-nowrap" v-else>System</div>
        </template>

        <!-- Column: Session -->
        <template #cell(pathway)="data">
          <div class="text-nowrap" v-if="data.item.pathway">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="32"
                  :src="data.item.pathway.avatar"
                  :text="avatarText(data.item.pathway.name)"
                  :variant="`${resolvePathwayStatusVariant(data.item.pathway.status)}`"
                  :to="{ name: 'apps-roster-pathway-view', params: { id: data.item.pathway.id } }"
                />
              </template>
              <b-link
                :to="{ name: 'apps-roster-pathway-view', params: { id: data.item.pathway.id } }"
                class="font-weight-bold d-block text-nowrap"
              >
                {{ data.item.pathway.name }}
              </b-link>
              <small v-if="data.item.pathwayDate" class="text-muted">
                {{ momemomentTznt(data.item.pathwayDate).format('MMM YYYY') }}
              </small>
              <small v-else class="text-muted"> No Preference </small>
            </b-media>
          </div>
          <div v-else-if="data.item.course">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="32"
                  :src="data.item.course.avatar"
                  :text="avatarText(data.item.course.name)"
                  :variant="`${resolveCourseStatusVariant(data.item.course.status)}`"
                  :to="{ name: 'apps-roster-course-view', params: { id: data.item.course.id } }"
                />
              </template>
              <b-link
                :to="{ name: 'apps-roster-course-view', params: { id: data.item.course.id } }"
                class="font-weight-bold d-block"
                style="inline-size: 300px"
              >
                {{ data.item.subject.name }}
              </b-link>
              <small v-if="data.item.pathwayDate" class="text-muted">
                {{ momentTz(data.item.pathwayDate).format('MMM YYYY') }}
              </small>
              <small v-else class="text-muted"> No Preference </small>
            </b-media>
          </div>
          <div class="text-nowrap" v-else>
            {{ data.item.metadata ? data.item.metadata.certificate || 'Not specified' : 'Not specified' }}
          </div>
        </template>
        <!-- Column: Session -->
        <template #cell(subject)="data">
          <div class="text-nowrap" v-if="data.item.subject">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="32"
                  :src="data.item.subject.avatar"
                  :text="avatarText(data.item.subject.name)"
                  :variant="`${resolvePathwayStatusVariant(data.item.subject.status)}`"
                  :to="{ name: 'apps-roster-course-view', params: { id: data.item.subject.id } }"
                />
              </template>
              <b-link
                :to="{ name: 'apps-roster-course-view', params: { id: data.item.subject.id } }"
                class="font-weight-bold d-block text-nowrap"
              >
                {{ data.item.subject.name }}
              </b-link>
              <small v-if="data.item.pathwayDate" class="text-muted">
                {{ momentTz(data.item.pathwayDate).format('MMM YYYY') }}
              </small>
              <small v-else class="text-muted"> No Preference </small>
            </b-media>
          </div>
          <div class="text-nowrap" v-else>
            {{ data.item.metadata ? data.item.metadata.certificate || 'Not specified' : 'Not specified' }}
          </div>
        </template>

        <!-- Column: Session -->
        <template #cell(session)="data">
          <div class="text-nowrap" v-if="data.item.session">
            <div
              v-if="data.item.session.type === 'pathway' && data.item.session.pathway"
              class="d-flex align-items-center text-nowrap"
              style="gap: 0.625rem"
            >
              <b-avatar
                size="36"
                :src="data.item.session.pathway.avatar"
                :text="avatarText(data.item.session.pathway.name)"
                :variant="`light-info`"
                :to="{ name: 'apps-roster-pathway-view', params: { id: data.item.session.pathway.id } }"
                style="margin-top: 2px"
                rounded
              />
              <div class="d-flex flex-column" style="gap: 0">
                <b-link
                  :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.session.id } }"
                  class="font-weight-bold text-nowrap text-white mb-0"
                >
                  {{ resolvePathwayCohortName(data.value) }}
                </b-link>
                <b-link
                  :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.session.id } }"
                  class="d-flex align-items-center text-muted text-nowrap"
                >
                  <lucide-icon name="Library" style="width: 14px; height: 14px; margin-right: 1px" />
                  <small> {{ data.item.session.pathway.name }}</small>
                </b-link>
              </div>
            </div>
            <div
              v-if="data.item.session.type === 'course' && data.item.session.course"
              class="d-flex align-items-center text-nowrap"
              style="gap: 0.625rem"
            >
              <b-avatar
                size="36"
                :src="data.item.session.course.avatar"
                :text="avatarText(data.item.session.course.name)"
                :variant="`light-info`"
                :to="{ name: 'apps-roster-course-view', params: { id: data.item.session.course.id } }"
                style="margin-top: 2px"
                rounded
              />
              <div class="d-flex flex-column" style="gap: 0">
                <b-link
                  :to="{ name: 'apps-roster-course-section-view', params: { id: data.item.session.id } }"
                  class="font-weight-bold text-nowrap text-white mb-0"
                >
                  {{ resolveCourseSectionName(data.value) }}
                </b-link>
                <b-link
                  :to="{ name: 'apps-roster-course-section-view', params: { id: data.item.session.id } }"
                  class="d-flex align-items-center text-muted text-nowrap"
                >
                  <lucide-icon name="Book" style="width: 14px; height: 14px; margin-right: 1px" />
                  <small> {{ data.item.session.course.name }}</small>
                </b-link>
              </div>
            </div>
          </div>
          <div class="text-nowrap d-flex align-items-center" v-else>
            <feather-icon
              :id="`profile-row-${data.item.id}-set-session-icon`"
              icon="PlusCircleIcon"
              class="cursor-pointer mr-25"
              size="16"
              @click="startSessionSelect(data.item)"
            />
            <b-tooltip title="Assign Session" class="cursor-pointer" :target="`profile-row-${data.item.id}-set-session-icon`" />
            Not Enrolled
          </div>
        </template>

        <!-- Column: Session -->
        <template #cell(email)="data">
          <span class="text-nowrap">{{ data.item.student.email }}</span>
        </template>

        <!-- Column: Attributes - Program -->
        <template #cell(fundingSource)="data">
          <b-badge
            :variant="`light-${resolveProfileProgramVariant(data.item.attributes.fundingSource)}`"
            class="text-nowrap text-capitalize"
          >
            {{ title(data.item.attributes.fundingSource) }}
          </b-badge>
        </template>

        <!-- Column: Attributes - Payment Status -->
        <template #cell(paymentStatus)="data">
          <b-badge
            :variant="`${resolveProfilePaymentStatusVariant(data.item.attributes.paymentStatus)}`"
            class="text-nowrap text-capitalize"
          >
            {{ title(data.item.attributes.paymentStatus) }}
          </b-badge>
        </template>

        <!-- Column: Attributes - Uploaded Resume -->
        <template #cell(uploadedResume)="data">
          <div class="text-nowrap">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-resume-icon`"
              icon="UploadIcon"
              class="cursor-pointer"
              size="16"
              @click="startUpload(data.item, 'enrollment', 'resume')"
            />

            <b-tooltip title="Upload Resume" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-resume-icon`" />
            &nbsp;
            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.attributes.uploadedResume)}`" class="text-capitalize">
              {{ title(data.item.attributes.uploadedResume) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - Uploaded COE -->
        <template #cell(uploadedCoe)="data">
          <div class="text-nowrap">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-coe-icon`"
              icon="UploadIcon"
              class="cursor-pointer"
              size="16"
              @click="startUpload(data.item, 'enrollment', 'coe')"
            />

            <b-tooltip title="Upload COE" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-coe-icon`" />
            &nbsp;
            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.attributes.uploadedCoe)}`" class="text-capitalize">
              {{ title(data.item.attributes.uploadedCoe) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - Uploaded Transcript -->
        <template #cell(uploadedTranscript)="data">
          <div class="text-nowrap">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-transcript-icon`"
              icon="UploadIcon"
              class="cursor-pointer"
              size="16"
              @click="startUpload(data.item, 'transcript', 'prior')"
            />

            <b-tooltip title="Upload Transcript" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-transcript-icon`" />
            &nbsp;
            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.attributes.uploadedTranscript)}`" class="text-capitalize">
              {{ title(data.item.attributes.uploadedTranscript) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - VA Submission -->
        <template #cell(submittedToVa)="data">
          <profile-submitted-to-va-select :profile="data.item" />
        </template>

        <!-- Column: Attributes - VA Submission -->
        <template #cell(toc)="data">
          <b-badge
            :variant="`light-${resolveProfileYNVariant(
              data.item.metadata && data.item.metadata.enrollmentToc ? (data.item.metadata.enrollmentToc.acceptAt ? 'yes' : 'no') : 'na',
            )}`"
            class="text-capitalize"
          >
            {{
              title(
                data.item.metadata && data.item.metadata.enrollmentToc ? (data.item.metadata.enrollmentToc.acceptAt ? 'Yes' : 'No') : 'N/A',
              )
            }}
          </b-badge>
        </template>

        <!-- Column: Session -->
        <template #cell(createdAt)="data">
          {{ momentTz(data.item.createdAt).format('MMMM Do YYYY') }}
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge :variant="`${resolveProfileStatusVariant(data.item.status)}`" class="text-capitalize">
            {{ title(data.item.status) }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="primary" class="dropdown-edit-button">
            <template #button-content>
              <div class="p-50">
                <feather-icon icon="MoreHorizontalIcon" size="21" />
              </div>
            </template>

            <b-dropdown-item
              v-if="$can('roster.profile.update')"
              @click="$router.push({ name: 'apps-roster-profile-edit', params: { id: data.item.id } })"
            >
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Edit</span>
                <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>

            <b-dropdown-item v-if="$can('roster.profile.delete')" @click="archiveProfile(data.item.id)">
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Archive</span>
                <feather-icon icon="ArchiveIcon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-50">
        <b-row>
          <b-col cols="12" md="3" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="mb-0 text-muted">{{ dataMeta.of }} Total Entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" md="9" class="d-flex align-items-center justify-content-center justify-content-sm-end mt-1 mt-sm-0">
            <div class="mr-2 vue-select-up">
              <span class="mb-0 mr-75">Rows Per Page</span>
              <v-select
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
                :open="true"
              />
            </div>
            <span class="mr-2 ml-lg-50 mb-0">Page {{ currentPage }} of {{ totalPageCount }}</span>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalProfiles"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-sm-0 ml-lg-50"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </div>

    <vue-context ref="rosterProfileMenu" class="mw-8" v-slot="{ data }">
      <li v-if="data">
        <a
          v-if="$can('roster.profile.update')"
          class="d-flex align-items-center justify-content-between"
          @click="$router.push({ name: 'apps-roster-profile-edit', params: { id: data.item.id } })"
        >
          <span>Edit</span>
          <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
        </a>
        <a
          v-if="$can('roster.profile.delete')"
          class="d-flex align-items-center justify-content-between"
          @click="archiveProfile(data.item.id)"
        >
          <span>Archive</span>
          <feather-icon icon="ArchiveIcon" size="15" class="ml-50 text-muted" />
        </a>
      </li>
    </vue-context>

    <profile-add-to-session :is-add-session-open.sync="isAddSessionOpen" v-on:refresh="refetchData" :profile="sessionProfile" />
    <profile-upload-document
      :is-upload-document-open.sync="upload.isUploadDocumentOpen"
      :profile="upload.profile"
      :document-category="upload.documentCategory"
      :document-type="upload.documentType"
      v-on:refresh="refetchData"
    />
  </div>
</template>

<script>
import ProfileSubmittedToVaSelect from '@/views/apps/roster/profile/components/ProfileSubmittedToVaSelect.vue';
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import { ref, onUnmounted } from '@vue/composition-api';
import { avatarText, title } from '@core/utils/filter';
import ProfileListFilters from './ProfileListFilters.vue';
import useUsersList from '../../../user/users-list/useUsersList';
import useProfiles from '../useProfiles';
import usePathways from '../../pathways/usePathways';
import useCourses from '../../courses/useCourses';
import rosterStoreModule from '../../../../../store/roster/rosterStoreModule';
import ProfileListAddv2 from '../create/ProfileListAddv2.vue';
import { momentTz } from '@/libs/utils/time';
import ProfileUploadDocument from '@/views/apps/roster/profile/list/components/ProfileUploadDocument.vue';
import ProfileAddToSession from '@/views/apps/roster/profile/list/components/ProfileAddToSession.vue';
import usePathwayCohorts from '../../pathways/cohorts/usePathwayCohorts';
import useCourseSections from '../../courses/sections/useCourseSections';
import VueContext from 'vue-context';

export default {
  components: {
    ProfileSubmittedToVaSelect,
    ProfileAddToSession,
    ProfileUploadDocument,
    ProfileListFilters,
    ProfileListAddv2,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,

    vSelect,
    VueContext,
  },
  data: () => {
    return {
      isAddSessionOpen: false,
      sessionProfile: undefined,

      upload: {
        isUploadDocumentOpen: false,

        profile: undefined,
        documentType: undefined,
        documentCategory: undefined,
      },
    };
  },
  methods: {
    onRowContextMenu(item, index, event) {
      this.$refs.rosterProfileMenu.open(event, { item });
      event.preventDefault();
    },
    startSessionSelect(profile) {
      this.sessionProfile = profile;
      this.isAddSessionOpen = true;
    },
    startUpload(profile, documentCategory, documentType) {
      this.upload = {
        isUploadDocumentOpen: true,
        profile,
        documentCategory,
        documentType,
      };
    },
    archiveProfile(profileId) {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to archive this profile?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          store
            .dispatch('rosterStoreModule/updateProfile', {
              id: profileId,
              profileData: {
                status: 'archived',
              },
            })
            .then((response) => {
              this.refetchData();
            });
        }
      });
    },
    newProfile() {
      this.createProfileActive = true;
    },
  },
  beforeDestroy() {
    this.$root.$off('fireRootEventN', this.newProfile);
  },
  mounted() {
    this.$root.$on('fireRootEventN', this.newProfile);
    if (this.$route.query.create === 'true') {
      this.newProfile();
    }
  },
  setup() {
    const { resolvePathwayCohortName } = usePathwayCohorts();

    const { resolveCourseSectionName } = useCourseSections();

    const createProfileActive = ref(false);

    const {
      fetchProfiles,
      tableColumnsList,
      perPage,
      currentPage,
      totalProfiles,
      totalPageCount,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refProfileListTable,
      refetchData,

      foresightOptions,
      statusOptions,
      fundingSourceOptions,

      submittedToVaOptions,
      uploadedCoeOptions,
      uploadedResumeOptions,
      uploadedTranscriptOptions,

      // UI
      resolveProfileVisibilityVariant,
      resolveProfileStatusVariant,
      resolveProfilePaymentStatusVariant,
      resolveProfileProgramVariant,
      resolveProfileYNVariant,

      // Extra Filters
      visibilityFilter,
      statusFilter,
      foresightFilter,
      includeEnrolledFilter,
      fundingFilter,

      resumeFilter,
      coeFilter,
      submissionFilter,

      createAtStartFilter,
      createAtEndFilter,
    } = useProfiles();

    const { resolveUserRoleVariant } = useUsersList();
    const { resolvePathwayStatusVariant } = usePathways();
    const { resolveCourseStatusVariant } = useCourses();

    return {
      // Sidebar
      createProfileActive,

      fetchProfiles,
      tableColumnsList,
      perPage,
      currentPage,
      totalPageCount,
      totalProfiles,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refProfileListTable,
      refetchData,

      // Filter
      avatarText,
      title,

      resolveUserRoleVariant,

      // UI

      resolveProfileVisibilityVariant,
      resolveProfileStatusVariant,

      resolveProfilePaymentStatusVariant,
      resolveProfileProgramVariant,
      resolveProfileYNVariant,

      resolvePathwayStatusVariant,
      resolveCourseStatusVariant,

      foresightOptions,
      statusOptions,
      fundingSourceOptions,

      submittedToVaOptions,
      uploadedCoeOptions,
      uploadedResumeOptions,
      uploadedTranscriptOptions,

      // Extra Filters
      visibilityFilter,
      statusFilter,
      foresightFilter,
      includeEnrolledFilter,
      fundingFilter,

      resumeFilter,
      coeFilter,
      submissionFilter,

      createAtStartFilter,
      createAtEndFilter,

      resolvePathwayCohortName,
      resolveCourseSectionName,

      //
      momentTz,
    };
  },
  metaInfo() {
    return {
      title: 'Roster Profiles',
    };
  },
};
</script>

<style lang="scss">
.per-page-selector {
  width: 90px;
}
</style>
